import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { InputMask } from 'primereact/inputmask';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import {
  useGetMyGroupsQuery,
  useUpdateMeMutation,
  useStartTelegramLinkMutation,
  // useLazyGetPushPublicKeyQuery,
  // useAddPushSubscriptionMutation,
  // useRemovePushSubscriptionMutation,
} from '../../../api/subscribeApi';
import { useToast } from '../../../context/ToastContext';
import { errMsg } from '../../../utils/errMsg';
import getSubdomain from '../../../utils/getSubdomain';
import { US_PHONE_MASK, phoneToDigits, digitsToPhone, isValidPhoneDigits } from '../../../utils/phone';
// import { subscribeToPush, unsubscribeFromPush } from '../../../utils/webPush';
import card from '../../../styles/shared/publicCard.module.css';
import styles from './SettingsPage.module.css';

const NOTIFICATION_INFO = {
  email: { icon: 'pi-envelope', label: 'Email', description: 'Get notified by email' },
  text: { icon: 'pi-mobile', label: 'SMS', description: 'Get notified by text message' },
  telegram: { icon: 'pi-telegram', label: 'Telegram', description: 'Get notified on Telegram' },
  // push: { icon: 'pi-bell', label: 'Push', description: 'Get browser push notifications on this device' },
};

export default function SettingsPage() {
  const { organizationSlug: slugFromPath } = useParams();
  const subdomainSlug = getSubdomain();
  const organizationSlug = slugFromPath ?? subdomainSlug;
  const loginPath = '/subscriber/login';
  const showToast = useToast();

  const [telegramLinkOpened, setTelegramLinkOpened] = useState(false);
  const { data, isLoading, isError, error } = useGetMyGroupsQuery(organizationSlug, {
    pollingInterval: telegramLinkOpened ? 3000 : 0,
  });
  const subscriber = data?.subscriber;
  const waitingForTelegram = telegramLinkOpened && !subscriber?.telegram_chat_id;
  const [updateMe, updateMeState] = useUpdateMeMutation();
  const [updateTelegramLink, updateTelegramLinkState] = useUpdateMeMutation();
  const [updateChannel, updateChannelState] = useUpdateMeMutation();
  const [startTelegramLink, telegramLinkState] = useStartTelegramLinkMutation();
  // const [getPushPublicKey] = useLazyGetPushPublicKeyQuery();
  // const [addPushSubscription] = useAddPushSubscriptionMutation();
  // const [removePushSubscription] = useRemovePushSubscriptionMutation();

  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  // const [pushBusy, setPushBusy] = useState(false);

  const enabledChannels = subscriber?.enabled_channels ?? [];

  function startEdit(field, currentValue) {
    setEditingField(field);
    setDraftValue(currentValue || '');
    setPhoneError(null);
  }

  function cancelEdit() {
    setEditingField(null);
    setDraftValue('');
    setPhoneError(null);
  }

  async function handleSavePhone() {
    const digits = draftValue || '';
    if (digits.length > 0 && !isValidPhoneDigits(digits)) {
      setPhoneError('Phone number must be 10 digits.');
      return;
    }
    setPhoneError(null);
    try {
      await updateMe({ organizationSlug, phone: digitsToPhone(digits) }).unwrap();
      showToast('success', 'Notifications', 'Phone number saved.');
      cancelEdit();
    } catch (err) {
      showToast('error', 'Notifications', errMsg(err, 'Could not save phone number.'));
    }
  }

  async function handleToggleChannel(channel) {
    const isOn = enabledChannels.includes(channel);
    if (!isOn) {
      if (channel === 'text' && !subscriber?.phone) {
        showToast('error', 'Notifications', 'Add a phone number above first.');
        return;
      }
      if (channel === 'telegram' && !subscriber?.telegram_chat_id) {
        showToast('error', 'Notifications', 'Connect Telegram above first.');
        return;
      }
    }
    const next = isOn
      ? enabledChannels.filter((c) => c !== channel)
      : [...enabledChannels, channel];
    try {
      await updateChannel({ organizationSlug, enabledChannels: next }).unwrap();
    } catch (err) {
      showToast('error', 'Notifications', errMsg(err, 'Could not update notification channel.'));
    }
  }

  // async function handleTogglePush() {
  //   if (pushBusy) return;
  //   const isOn = enabledChannels.includes('push');
  //   setPushBusy(true);
  //   try {
  //     if (isOn) {
  //       await unsubscribeFromPush({ organizationSlug, removePushSubscription });
  //       await updateChannel({
  //         organizationSlug,
  //         enabledChannels: enabledChannels.filter((c) => c !== 'push'),
  //       }).unwrap();
  //       return;
  //     }
  //
  //     await subscribeToPush({ organizationSlug, getPushPublicKey, addPushSubscription });
  //     await updateChannel({ organizationSlug, enabledChannels: [...enabledChannels, 'push'] }).unwrap();
  //     showToast('success', 'Notifications', 'Push notifications enabled on this device.');
  //   } catch (err) {
  //     showToast('error', 'Notifications', errMsg(err, 'Could not update push notifications.'));
  //   } finally {
  //     setPushBusy(false);
  //   }
  // }

  async function handleConnectTelegram() {
    try {
      const { deep_link_url: deepLinkUrl } = await startTelegramLink({ organizationSlug }).unwrap();
      window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
      setTelegramLinkOpened(true);
    } catch (err) {
      showToast('error', 'Notifications', errMsg(err, 'Telegram linking is not available yet.'));
    }
  }

  async function handleUnlinkTelegram() {
    try {
      await updateTelegramLink({ organizationSlug, telegramChatId: null }).unwrap();
      showToast('success', 'Notifications', 'Telegram disconnected.');
      setTelegramLinkOpened(false);
    } catch (err) {
      showToast('error', 'Notifications', errMsg(err, 'Could not disconnect Telegram.'));
    }
  }

  if (isError) {
    return (
      <div className={card.centeredPage}>
        <p className={card.status}>
          {error?.status === 401
            ? 'Your session expired.'
            : 'Could not load your settings. Please try again.'}
        </p>
        <Link to={loginPath}>Log in</Link>
      </div>
    );
  }

  if (isLoading) return <p className={card.status}>Loading…</p>;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="pi pi-address-book" />
          <span className={styles.cardHeaderTitle}>Contact methods</span>
        </div>

        <div className={styles.fieldsList}>
          <div className={styles.field}>
            <div className={styles.fieldContent}>
              <div className={styles.fieldLabel}>Email (default)</div>
              <div className={`${styles.fieldValue} ${styles.fieldValueDisabled}`}>
                {subscriber?.email}
              </div>
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldContent}>
              <div className={styles.fieldLabel}>Phone number</div>
              {editingField === 'phone' ? (
                <>
                  <div className={styles.editInputRow}>
                    <InputMask
                      className={styles.editInput}
                      mask={US_PHONE_MASK}
                      unmask
                      autoClear={false}
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.value || '')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSavePhone();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                    />
                    <div className={styles.editActions}>
                      <Button
                        type="button"
                        icon="pi pi-check"
                        rounded
                        text
                        severity="success"
                        onClick={handleSavePhone}
                        loading={updateMeState.isLoading}
                        aria-label="Save"
                      />
                      <Button
                        type="button"
                        icon="pi pi-times"
                        rounded
                        text
                        severity="secondary"
                        onClick={cancelEdit}
                        disabled={updateMeState.isLoading}
                        aria-label="Cancel"
                      />
                    </div>
                  </div>
                  {phoneError && <small className={styles.fieldError}>{phoneError}</small>}
                </>
              ) : (
                <div className={styles.fieldValue}>{subscriber?.phone || '—'}</div>
              )}
            </div>
            {editingField !== 'phone' && (
              <button
                type="button"
                className={styles.editButton}
                onClick={() => startEdit('phone', phoneToDigits(subscriber?.phone))}
                aria-label="Edit phone number"
              >
                <i className="pi pi-pencil" />
              </button>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.fieldContent}>
              <div className={styles.fieldLabel}>Telegram</div>
              <div className={styles.fieldValue}>
                {subscriber?.telegram_chat_id
                  ? 'Connected'
                  : waitingForTelegram
                    ? 'Waiting for connection…'
                    : '—'}
              </div>
            </div>
            <button
              type="button"
              className={`${styles.editButton} ${styles.telegramActionBtn}`}
              onClick={subscriber?.telegram_chat_id ? handleUnlinkTelegram : handleConnectTelegram}
              disabled={telegramLinkState.isLoading || updateTelegramLinkState.isLoading}
              aria-label={subscriber?.telegram_chat_id ? 'Disconnect Telegram' : 'Connect Telegram'}
            >
              <i className={`pi ${subscriber?.telegram_chat_id ? 'pi-times' : 'pi-link'}`} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.cardSpacer} />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="pi pi-bell" />
          <span className={styles.cardHeaderTitle}>Notifications</span>
        </div>

        {Object.entries(NOTIFICATION_INFO).map(([channel, info]) => (
          <div key={channel} className={styles.subCard}>
            <div className={styles.iconBox}>
              <i className={`pi ${info.icon}`} />
            </div>
            <div className={styles.subCardText}>
              <span className={styles.subCardTitle}>{info.label}</span>
              <span className={styles.subCardDescription}>{info.description}</span>
            </div>
            <InputSwitch
              checked={enabledChannels.includes(channel)}
              disabled={updateChannelState.isLoading}
              onChange={() => handleToggleChannel(channel)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
